// PR-1 — Receive: what arrived, how many, where it is, what state it is in.
// One sheet, in the shell's right-hand dock, used from the clip page and from
// an order line. Saving can print the tags there and then, and a damaged
// receipt hands on to the note to the vendor.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DockPanel from '@/components/shell/DockPanel';
import { MaterialTagForm } from '@/components/MaterialTagForm';
import '@/styles/receiving.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WHERE_TILES = [
  ['office', 'Office'], ['site', 'Site'], ['warehouse', 'Warehouse'],
  ['vendor', 'Vendor holds'], ['contractor', 'Contractor took it'],
];
const CONDITION_TILES = [
  ['good', 'Good'], ['damaged', 'Damaged — photo'], ['short', 'Short — reorder'],
];
const today = () => new Date().toISOString().slice(0, 10);

const Tiles = ({ rows, value, onPick, test }) => (
  <div className="kpr-tiles">
    {rows.map(([key, label]) => (
      <button type="button" key={key}
        className={`kpr-tile${value === key ? ' on' : ''}`}
        onClick={() => onPick(key)} data-testid={`${test}-${key}`}>{label}</button>
    ))}
  </div>
);

// The note to the vendor about what arrived damaged — drafted by the server,
// edited here, sent or left.
const VendorNote = ({ clipId, onClose }) => {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/clips/${clipId}/receive/vendor-note-draft`)
      .then((r) => setDraft(r.data))
      .catch(() => setDraft({ to: '', subject: '', body: '' }));
  }, [clipId]);

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/clips/${clipId}/receive/vendor-note`, draft);
      toast.success(`Damage note sent to ${draft.to}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That note did not send');
    } finally { setBusy(false); }
  };

  if (!draft) return null;
  const set = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });
  return (
    <div className="kpr-sheet" data-testid="receive-vendor-note">
      <div className="kpr-card">
        <div className="kp-head">
          <div>
            <div className="kpr-k">Damaged on arrival</div>
            <div className="t">The note to the vendor</div>
          </div>
        </div>
        <label className="kpr-f">
          <span className="kpr-k">To</span>
          <input className="kpr-in" value={draft.to} onChange={set('to')}
            placeholder="rep@vendor.com" data-testid="vendor-note-to" />
          {!draft.to ? (
            <p className="kpr-hint">This material is on no order, so there is no
              address on file — put one in.</p>
          ) : null}
        </label>
        <label className="kpr-f">
          <span className="kpr-k">Subject</span>
          <input className="kpr-in" value={draft.subject} onChange={set('subject')}
            data-testid="vendor-note-subject" />
        </label>
        <label className="kpr-f">
          <span className="kpr-k">Note</span>
          <textarea className="kpr-in kpr-note" value={draft.body} onChange={set('body')}
            data-testid="vendor-note-body" />
          {draft.photos?.length ? (
            <p className="kpr-hint" data-testid="vendor-note-photos">
              {draft.photos.length} photo{draft.photos.length === 1 ? '' : 's'} attached
            </p>
          ) : null}
        </label>
        <div className="kpr-acts">
          <button type="button" className="kpr-btn kpr-btn--brand" disabled={busy}
            onClick={send} data-testid="vendor-note-send">
            {busy ? 'Sending…' : 'Send'}
          </button>
          <button type="button" className="kpr-btn" onClick={onClose}
            data-testid="vendor-note-later">Not now</button>
        </div>
      </div>
    </div>
  );
};

export const ReceiveSheet = ({ clip, poId, poItemId, of = 1, received = 0,
                               edit = null, onClose, onSaved }) => {
  // PR-1b — the count opens on what is still owed, so the usual delivery is
  // one tap. An Edit opens on what that receipt said.
  const [form, setForm] = useState({
    count: String(edit ? (edit.count ?? 1) : Math.max(1, Number(of) - Number(received || 0))),
    date: String(edit?.received_at || '').slice(0, 10) || today(),
    where: edit?.where || 'office', location: edit?.location || '',
    condition: edit?.condition || 'good', tags: edit ? 'later' : 'now',
    note: edit?.note || '',
  });
  const [spots, setSpots] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [touched, setTouched] = useState(false);
  const [over, setOver] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [noting, setNoting] = useState(false);
  const file = useRef(null);

  useEffect(() => {
    axios.get(`${API}/company/recent-locations`)
      .then((r) => setSpots(r.data?.locations || []))
      .catch(() => setSpots([]));
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pick = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const addPhoto = async (chosen) => {
    if (!chosen || !clip?.id) return;
    const body = new FormData();
    body.append('file', chosen);
    try {
      const r = await axios.post(`${API}/clips/${clip.id}/receive/photo`, body);
      setPhotos(r.data?.photos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That photo did not go up');
    }
  };

  // PR-1b — a whole positive number, said under the field, never in a toast
  // the task float can sit on top of.
  const typed = form.count.trim();
  const counted = Number(typed);
  const sound = !!typed && Number.isInteger(counted) && counted > 0;
  const said = problem
    || (touched && !sound ? 'Say how many arrived — a whole number, one or more' : '');

  const send = useCallback(async (andNext) => {
    const count = Number(form.count);
    setBusy(true);
    try {
      const body = {
        count, where: form.where, location: form.location.trim(),
        condition: form.condition, note: form.note.trim(),
        received_at: form.date ? `${form.date}T12:00:00+00:00` : undefined,
        ...(poItemId ? { po_item_id: poItemId } : {}),
        ...(edit ? { replace_last: true } : {}),
      };
      if (clip?.id) {
        await axios.post(`${API}/clips/${clip.id}/receive`, body);
      } else {
        await axios.post(`${API}/purchase-orders/${poId}/items/${poItemId}/receive`, body);
      }
      toast.success(`Received ${count} of ${of}`);
      onSaved?.();
      if (form.condition === 'damaged' && clip?.id) { setNoting(true); return; }
      if (form.tags === 'now' && clip?.id) { setPrinting(true); return; }
      if (andNext) {
        // The rest of the delivery lands in the same place, so those stay.
        setForm((f) => ({ ...f, count: '', note: '', condition: 'good' }));
        setPhotos([]);
        return;
      }
      onClose?.();
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That receipt did not save');
    } finally { setBusy(false); }
  }, [form, clip, poId, poItemId, of, edit, onSaved, onClose]);

  const save = (andNext) => {
    if (!sound) { setProblem('Say how many arrived — a whole number, one or more'); return; }
    setProblem('');
    const already = edit ? Number(received || 0) - Number(edit.count || 0) : Number(received || 0);
    if (counted + already > Number(of)) { setOver(andNext ? 'next' : 'save'); return; }
    send(andNext);
  };

  const count = Number(form.count) || 1;

  return (
    // A page without the shell gives the dock its own frame, so the width has
    // to cap at the screen or the sheet runs off a phone.
    <DockPanel onClose={onClose} width="min(460px, 100vw)">
      <aside className="kpr" data-testid="receive-sheet">
        <div className="kp-head">
          <div>
            <div className="k">{edit ? 'Edit the receipt' : 'Receive'}</div>
            <div className="t">{clip?.title || clip?.name || 'This line'}</div>
          </div>
          <button type="button" className="kpr-btn" onClick={onClose}
            data-testid="receive-close">Close</button>
        </div>

        <label className="kpr-f">
          <span className="kpr-k">Count received</span>
          <span className="kpr-row">
            <input className="kpr-in" type="number" min="1" value={form.count}
              onChange={(e) => { setProblem(''); setTouched(true); set('count')(e); }}
              data-testid="receive-count" />
            <span className="kpr-of" data-testid="receive-of">of {of}</span>
          </span>
          {said ? (
            <p className="kpr-err" data-testid="receive-count-error">{said}</p>
          ) : null}
        </label>

        <label className="kpr-f">
          <span className="kpr-k">Date</span>
          <input className="kpr-in" type="date" value={form.date}
            onChange={set('date')} data-testid="receive-date" />
        </label>

        <div className="kpr-f">
          <span className="kpr-k">Where</span>
          <Tiles rows={WHERE_TILES} value={form.where} onPick={pick('where')}
            test="receive-where" />
        </div>

        <label className="kpr-f">
          <span className="kpr-k">Location</span>
          <input className="kpr-in" value={form.location} onChange={set('location')}
            list="kpr-spots" placeholder="shelf, bay, garage…"
            data-testid="receive-location" />
          <datalist id="kpr-spots">
            {spots.map((spot) => <option key={spot} value={spot} />)}
          </datalist>
          {/* PR-1c — this is the carton's whereabouts, not the install spot. */}
          <p className="kpr-hint" data-testid="receive-location-caption">
            Where it is now — shelf, bay, garage… (not where it will be installed)
          </p>
        </label>

        <div className="kpr-f">
          <span className="kpr-k">Condition</span>
          <Tiles rows={CONDITION_TILES} value={form.condition} onPick={pick('condition')}
            test="receive-condition" />
          {form.condition === 'damaged' ? (
            <>
              <input className="kpr-in" type="file" accept="image/*" ref={file}
                onChange={(e) => addPhoto(e.target.files?.[0])}
                data-testid="receive-photo" />
              {photos.length ? (
                <div className="kpr-shots" data-testid="receive-photos">
                  {photos.map((photo) => (
                    <img key={photo.url} src={photo.url} alt={photo.filename || ''} />
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
          {form.condition === 'short' ? (
            <input className="kpr-in" value={form.note} onChange={set('note')}
              placeholder="how many short" data-testid="receive-short" />
          ) : null}
        </div>

        <div className="kpr-f">
          <span className="kpr-k">Tags</span>
          <Tiles rows={[['now', `Print ${count} tag${count === 1 ? '' : 's'} now`],
                        ['later', 'Later']]}
            value={form.tags} onPick={pick('tags')} test="receive-tags" />
        </div>

        <label className="kpr-f">
          <span className="kpr-k">Note</span>
          <input className="kpr-in" value={form.note} onChange={set('note')}
            placeholder="anything the next person should know"
            data-testid="receive-note" />
        </label>

        <div className="kpr-acts">
          <button type="button" className="kpr-btn kpr-btn--brand"
            disabled={busy || !sound}
            onClick={() => save(false)} data-testid="receive-save">
            {busy ? 'Saving…' : 'Received'}
          </button>
          <button type="button" className="kpr-btn" disabled={busy || !sound}
            onClick={() => save(true)} data-testid="receive-next">
            Received — next item
          </button>
        </div>

        {over ? (
          <div className="kpr-sheet" data-testid="receive-over-sheet">
            <div className="kpr-card">
              <div className="kpr-k">More than expected — record it?</div>
              <p className="kpr-hint">
                {`${counted} of ${of} expected. The count stands as the truth of what
                  is in the building, and the order line follows it.`}
              </p>
              <div className="kpr-acts">
                <button type="button" className="kpr-btn kpr-btn--brand" disabled={busy}
                  onClick={() => { const next = over; setOver(null); send(next === 'next'); }}
                  data-testid="receive-over-yes">Record it</button>
                <button type="button" className="kpr-btn" onClick={() => setOver(null)}
                  data-testid="receive-over-no">Go back</button>
              </div>
            </div>
          </div>
        ) : null}

        {printing && clip?.project_id ? (
          <MaterialTagForm
            projectId={clip.project_id}
            source="clip"
            body={{ clip_id: clip.id }}
            title={clip.title || clip.name || ''}
            room={clip.room || ''}
            roomId={clip.room_id || null}
            location={clip.room_location || ''}
            tradePrefix={clip.spec_category || ''}
            trade={clip.last_tag_trade || ''}
            tagName={clip.tag_name || ''}
            tagNote={clip.tag_note || ''}
            // SP-2 — the whole order is How many; the box that landed is the
            // first package.
            count={Math.max(count, Number(of) || 1)}
            firstPackage={count}
            imageCount={(clip.images || []).length}
            onMade={() => onSaved?.()}
            onClose={() => { setPrinting(false); onClose?.(); }}
          />
        ) : null}

        {noting && clip?.id ? (
          <VendorNote clipId={clip.id}
            onClose={() => { setNoting(false); onClose?.(); }} />
        ) : null}
      </aside>
    </DockPanel>
  );
};

export default ReceiveSheet;
